import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

type FieldItem = {
  id: number | null;
  column_name: string;
  description: string | null;
  isRequired: boolean;
  isShown: boolean;
};

interface ViewProps {
  refreshFields: any;
}

const formatLabel = (str: string) =>
  str.replace(/_/g, ' ').replace(/^./, (s) => s.toUpperCase());

const thStyle: React.CSSProperties = {
  color: 'var(--text-color)',
  fontSize: 11,
  padding: '10px 14px',
  backgroundColor: 'var(--background-color)',
  borderBottom: '1px solid var(--border-input)',
  textAlign: 'left',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  color: 'var(--text-color)',
  fontSize: 13,
  padding: '6px 14px',
  backgroundColor: 'var(--btn-user-form)',
  borderBottom: '1px solid var(--border-input)',
};

export default function ViewSetting({ refreshFields }: ViewProps) {
  const [fields, setFields] = useState<FieldItem[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('fields');
        if (Array.isArray(data)) {
          setFields(data.filter((f: any) => f.table_name === 'expenses'));
        }
      } catch {}
    })();
  }, []);

  const save = async (field: FieldItem) => {
    setSaving(field.column_name);
    try {
      const payload = {
        table_name: 'expenses',
        column_name: field.column_name,
        isRequired: field.isRequired,
        isShown: field.isShown,
      };
      if (field.id) {
        await axios.put(`fields/${field.id}`, payload);
      } else {
        const res = await axios.post('fields/add', payload);
        const newId = res.data?.id;
        if (newId) {
          setFields(prev => prev.map(f =>
            f.column_name === field.column_name ? { ...f, id: newId } : f
          ));
        }
      }
      refreshFields();
    } finally {
      setSaving(null);
    }
  };

  const startEdit = (field: FieldItem) => {
    setEditingId(field.id);
    setEditName(field.column_name);
    setEditDesc(field.description ?? '');
  };

  const cancelEdit = () => { setEditingId(null); };

  const saveEdit = async (field: FieldItem) => {
    if (!field.id) return;
    setEditSaving(true);
    try {
      await axios.put(`fields/${field.id}`, {
        table_name: 'expenses',
        column_name: editName.trim() || field.column_name,
        description: editDesc.trim() || null,
        isRequired: field.isRequired,
        isShown: field.isShown,
      });
      setFields(prev => prev.map(f =>
        f.id === field.id
          ? { ...f, column_name: editName.trim() || f.column_name, description: editDesc.trim() || null }
          : f
      ));
      setEditingId(null);
      refreshFields();
    } catch {} finally {
      setEditSaving(false);
    }
  };

  const toggle = (column_name: string, key: 'isShown' | 'isRequired') => {
    setFields(prev => {
      const updated = prev.map(f => {
        if (f.column_name !== column_name) return f;
        const next = { ...f, [key]: !f[key] };
        // if hiding, also unset required
        if (key === 'isShown' && !next.isShown) next.isRequired = false;
        save(next);
        return next;
      });
      return updated;
    });
  };

  return (
    <div className="bg-[var(--bg-form)] rounded-[5px] overflow-hidden">
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={thStyle}>{t('Field')}</th>
            <th style={{ ...thStyle, textAlign: 'center', width: 90 }}>{t('Visible')}</th>
            <th style={{ ...thStyle, textAlign: 'center', width: 90 }}>{t('Required')}</th>
            <th style={{ ...thStyle, width: 44 }} />
          </tr>
        </thead>
        <tbody>
          {fields.map(field => (
            <React.Fragment key={field.column_name}>
              <tr>
                <td style={tdStyle}>
                  <div style={{ opacity: field.isShown ? 1 : 0.45 }}>
                    <span>{formatLabel(field.column_name)}</span>
                    {field.description && (
                      <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                        {field.description}
                      </div>
                    )}
                  </div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <Switch
                    size="small"
                    checked={field.isShown}
                    disabled={saving === field.column_name}
                    onChange={() => toggle(field.column_name, 'isShown')}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <Switch
                    size="small"
                    checked={field.isRequired}
                    disabled={!field.isShown || saving === field.column_name}
                    onChange={() => toggle(field.column_name, 'isRequired')}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center', padding: '2px 6px' }}>
                  <IconButton size="small" onClick={() => startEdit(field)}
                    sx={{ opacity: 0.55, '&:hover': { opacity: 1 } }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </td>
              </tr>
              {editingId === field.id && (
                <tr>
                  <td colSpan={4} style={{ ...tdStyle, padding: '10px 14px', backgroundColor: 'var(--background-color)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>
                          {t('FieldName') || 'Field name'}
                        </div>
                        <input
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="fx-input"
                          style={{ width: '100%' }}
                          autoFocus
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>
                          {t('Description') || 'Description'}
                        </div>
                        <input
                          value={editDesc}
                          onChange={e => setEditDesc(e.target.value)}
                          className="fx-input"
                          placeholder={t('DescriptionOptional') || 'Optional description…'}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={cancelEdit} disabled={editSaving}
                          sx={{ border: '1px solid var(--border-input)' }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => saveEdit(field)} disabled={editSaving}
                          sx={{ border: '1px solid var(--border-input)', color: 'var(--color-success)' }}>
                          <CheckIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {fields.length === 0 && (
            <tr>
              <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', opacity: 0.5 }}>
                {t('Empty') || 'No fields'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
